import {
  CSSObjectWithLabel,
  GroupBase,
  default as ReactSelect,
  Props as ReactSelectProps,
  Theme
} from 'react-select';
import { useTheme } from 'styled-components';

export interface SelectOption<Option = string | number> {
  value: Option;
  label: string;
}

export type SelectProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<SelectOption<Option>> = GroupBase<
    SelectOption<Option>
  >
> = ReactSelectProps<SelectOption<Option>, IsMulti, Group>;

export function Select<
  Option = string | number,
  IsMulti extends boolean = false,
  Group extends GroupBase<SelectOption<Option>> = GroupBase<
    SelectOption<Option>
  >
>(props: SelectProps<Option, IsMulti, Group>): JSX.Element {
  const theme = useTheme();

  return (
    <ReactSelect
      id={props.name}
      theme={(defaultTheme): Theme => ({
        ...defaultTheme,
        colors: {
          ...defaultTheme.colors,
          primary: theme.palette_blue_100,
          primary25: theme.palette_blue_5
        }
      })}
      styles={{
        container: (provided) =>
          ({
            ...provided,
            height: '47px',
            minWidth: '164px'
          }) as CSSObjectWithLabel,
        control: (provided, state) =>
          ({
            ...provided,
            height: '47px',
            borderRadius: theme.border_radius_x1,
            borderColor: theme.palette_gray_5,
            paddingLeft: theme.spacing_unit_x2,
            paddingRight: theme.spacing_unit_x2,
            boxSizing: 'border-box',
            transition: 'none',
            boxShadow: 'none',
            outline: 'unset',
            '&:hover': {
              borderColor: theme.palette_gray_5
            },
            '&:focus-within': {
              outlineStyle: 'solid',
              outlineWidth: '2px!important',
              outlineColor: theme.palette_blue_5
            },
            ...(state.isDisabled
              ? {
                  color: theme.palette_gray_60,
                  backgroundColor: theme.palette_gray_5
                }
              : {})
          }) as CSSObjectWithLabel,
        valueContainer: (provided) =>
          ({
            ...provided,
            padding: 0
          }) as CSSObjectWithLabel,
        singleValue: (provided) =>
          ({
            ...provided,
            margin: 0
          }) as CSSObjectWithLabel,
        input: (provided) =>
          ({
            ...provided,
            margin: 0,
            padding: 0
          }) as CSSObjectWithLabel,
        indicatorSeparator: () =>
          ({
            display: 'none'
          }) as CSSObjectWithLabel,
        dropdownIndicator: (provided) =>
          ({
            ...provided,
            padding: 0,
            color: 'inherit'
          }) as CSSObjectWithLabel,
        placeholder: (provided) =>
          ({
            ...provided,
            color: 'inherit'
          }) as CSSObjectWithLabel
      }}
      {...props}
    />
  );
}
